import React, { useRef, useState, useEffect } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  TextField,
  IconButton,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  LinearProgress,
  Switch,
  Tab,
  Tabs,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Snackbar,
  SnackbarContent,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Slide from "@material-ui/core/Slide";
import { parseJSONfromText } from "../../author/hooks/useImportPlans";
import { firestore } from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";
import { Community } from "../../../../types/Community";
import { Author } from "../../../../types/Author";
import {
  generateSingleLearningPlan,
  UploadedFile as OnboardingUploadedFile,
} from "../../onboarding/OnboardingAPI";
import { extractLearningPlanPreserveText } from "../../onboarding/extractLearningPlanPreserveText";
import {
  generateWorkoutProgram,
  WorkoutProgramDraft,
} from "./workoutProgramApi";
import { createWorkoutProgram } from "../../../../database/plans/createWorkoutProgram";
import getAllHabits from "../../../../database/habits/getAllHabits";
import {
  PLAN_SUBCATEGORIES,
  findSubcategoryById,
} from "../../../../data/planSubcategories";

type Message = {
  role: "user" | "assistant";
  content: string;
};

const renderSlide = (props: any) => <Slide direction="up" {...props} />;

// Heuristic mapping from common program-name keywords to the curated
// exercise subcategory ids. Used to seed the importer dropdown so the admin
// usually doesn't have to think about it.
const WORKOUT_SUBCATEGORY_KEYWORDS: Array<{ pattern: RegExp; id: string }> = [
  { pattern: /\b(back|taper|lat|row)\b/i, id: "back" },
  { pattern: /\b(arm|bicep|tricep|armed)\b/i, id: "arms" },
  { pattern: /\b(leg|squat|stomp|quad|hamstring|glute)\b/i, id: "legs" },
  { pattern: /\b(chest|pec|thick|bench)\b/i, id: "chest" },
  { pattern: /\b(shoulder|delt|boulder|press)\b/i, id: "shoulders" },
  { pattern: /\b(push.?pull.?legs?|ppl)\b/i, id: "push_pull_legs" },
  { pattern: /\b(upper.?lower|ul)\b/i, id: "upper_lower" },
  { pattern: /\b(power.?build|apex|strength)\b/i, id: "powerbuilding" },
  { pattern: /\b(hypertroph|muscle.?build)\b/i, id: "hypertrophy" },
  { pattern: /\b(full.?body|total.?body)\b/i, id: "full_body" },
];

const inferWorkoutSubcategory = (
  ...candidates: Array<string | string[] | undefined>
): string | undefined => {
  const haystack = candidates
    .flat()
    .filter((s): s is string => typeof s === "string")
    .join(" ");
  if (!haystack) return undefined;
  for (const { pattern, id } of WORKOUT_SUBCATEGORY_KEYWORDS) {
    if (pattern.test(haystack)) return id;
  }
  return undefined;
};

const formatReps = (reps: any): string | null => {
  if (reps == null) return null;
  if (reps === "max") return "Max reps";
  if (Array.isArray(reps) && reps.length === 2) {
    return reps[0] === reps[1] ? `${reps[0]} reps` : `${reps[0]}-${reps[1]} reps`;
  }
  if (typeof reps === "object") {
    if (reps.kind === "range" && typeof reps.min === "number" && typeof reps.max === "number") {
      return reps.min === reps.max ? `${reps.min} reps` : `${reps.min}-${reps.max} reps`;
    }
    if (typeof reps.seconds === "number") return `${reps.seconds} sec`;
  }
  return null;
};

const formatRange = (range?: [number, number]): string | null => {
  if (!Array.isArray(range) || range.length !== 2) return null;
  return range[0] === range[1] ? `${range[0]}` : `${range[0]}-${range[1]}`;
};

const formatTempo = (tempo?: number[]): string | null => {
  if (!Array.isArray(tempo) || tempo.length === 0) return null;
  return tempo.join("-");
};

const blockHeaderLabel = (block: { type: string; label?: string }): string | null => {
  if (block.type === "single") return null;
  if (block.label?.trim()) return block.label.trim();
  if (block.type === "superset") return "Superset";
  if (block.type === "giant_set") return "Giant set";
  return null;
};

const isWarmupSet = (s: any): boolean => s?.percent != null;

const CALLOUT_STYLE: Record<string, { bg: string; border: string }> = {
  info: { bg: "#e3f2fd", border: "#1976d2" },
  warning: { bg: "#fff3e0", border: "#ef6c00" },
  tip: { bg: "#e8f5e9", border: "#2e7d32" },
  note: { bg: "#f3e5f5", border: "#6a1b9a" },
};

const StepPreview = ({ step, index }: { step: any; index: number }) => {
  const bodyText: string =
    (typeof step.text === "string" && step.text.trim()) ||
    (typeof step.description === "string" && step.description.trim()) ||
    "";
  const questionCount = Array.isArray(step.questions) ? step.questions.length : 0;
  const imageCount = Array.isArray(step.images) ? step.images.length : 0;
  const lists: any[] = Array.isArray(step.lists) ? step.lists : [];
  const callouts: any[] = Array.isArray(step.callouts) ? step.callouts : [];
  const xp = typeof step.xpReward === "number" ? step.xpReward : 0;
  const showType = step.type && step.type !== "DEFAULT";

  return (
    <Box mb={2} pl={1}>
      <Typography variant="body1" style={{ fontWeight: 600 }}>
        {index + 1}. {step.title || "Untitled step"}
      </Typography>
      {step.subtitle && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ display: "block" }}
        >
          {step.subtitle}
        </Typography>
      )}
      {step.sectionTitle && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", fontStyle: "italic" }}
        >
          Section: {step.sectionTitle}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 4 }}>
        {showType && <Chip size="small" label={step.type} variant="outlined" />}
        {xp > 0 && <Chip size="small" label={`${xp} XP`} variant="outlined" />}
        {questionCount > 0 && (
          <Chip
            size="small"
            label={`${questionCount} question${questionCount === 1 ? "" : "s"}`}
            variant="outlined"
          />
        )}
        {imageCount > 0 && (
          <Chip
            size="small"
            label={`${imageCount} image${imageCount === 1 ? "" : "s"}`}
            variant="outlined"
          />
        )}
        {lists.length > 0 && (
          <Chip
            size="small"
            label={`${lists.length} list${lists.length === 1 ? "" : "s"}`}
            variant="outlined"
          />
        )}
        {callouts.length > 0 && (
          <Chip
            size="small"
            label={`${callouts.length} callout${
              callouts.length === 1 ? "" : "s"
            }`}
            variant="outlined"
          />
        )}
      </Box>
      {bodyText && (
        <Typography
          variant="body2"
          color="textPrimary"
          style={{
            display: "block",
            marginTop: 8,
            whiteSpace: "pre-wrap",
            lineHeight: 1.55,
          }}
        >
          {bodyText}
        </Typography>
      )}
      {lists.map((list, li) => {
        const items: string[] = Array.isArray(list.items)
          ? list.items.filter((s: any): s is string => typeof s === "string")
          : [];
        if (!items.length) return null;
        const ListTag: "ol" | "ul" = list.ordered ? "ol" : "ul";
        return (
          <Box key={`list-${li}`} mt={1.5}>
            {list.title && (
              <Typography
                variant="body2"
                style={{ fontWeight: 600, marginBottom: 4 }}
              >
                {list.title}
              </Typography>
            )}
            <ListTag style={{ margin: 0, paddingLeft: 20 }}>
              {items.map((item, ii) => (
                <li key={ii}>
                  <Typography variant="body2" style={{ lineHeight: 1.55 }}>
                    {item}
                  </Typography>
                </li>
              ))}
            </ListTag>
          </Box>
        );
      })}
      {callouts.map((c, ci) => {
        const text =
          typeof c.text === "string" && c.text.trim() ? c.text.trim() : "";
        if (!text) return null;
        const style = CALLOUT_STYLE[c.tone as string] || CALLOUT_STYLE.note;
        return (
          <Box
            key={`callout-${ci}`}
            mt={1.5}
            p={1.5}
            style={{
              backgroundColor: style.bg,
              borderLeft: `3px solid ${style.border}`,
              borderRadius: 4,
            }}
          >
            {c.title && (
              <Typography
                variant="caption"
                style={{
                  fontWeight: 700,
                  display: "block",
                  textTransform: "uppercase",
                  color: style.border,
                  marginBottom: 4,
                }}
              >
                {c.title}
              </Typography>
            )}
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
              {text}
            </Typography>
          </Box>
        );
      })}
      {step.quote && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{
            display: "block",
            marginTop: 8,
            fontStyle: "italic",
            borderLeft: "3px solid #bdbdbd",
            paddingLeft: 8,
          }}
        >
          “{step.quote}”
        </Typography>
      )}
      {step.reference && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 6 }}
        >
          Ref: {step.reference}
        </Typography>
      )}
    </Box>
  );
};

const SubactionPreview = ({ sub }: { sub: any }) => {
  const p = sub.prescription ?? {};
  const sets: any[] = Array.isArray(p.sets) ? p.sets : [];
  const warmupCount = sets.filter(isWarmupSet).length;
  const workingCount = sets.length - warmupCount;
  const lastSet = sets[sets.length - 1];
  const tempoText = formatTempo(p.tempo);
  const restText = formatRange(p.restMin);
  const dropRepsText =
    Array.isArray(lastSet?.dropReps) && lastSet.dropReps.length
      ? lastSet.dropReps.join("+")
      : null;
  const miniSetRepsText =
    Array.isArray(lastSet?.miniSetReps) && lastSet.miniSetReps.length
      ? lastSet.miniSetReps.join("+")
      : null;
  const alternatives = Array.isArray(sub.alternatives)
    ? sub.alternatives.map((a: any) => a.name).filter(Boolean)
    : [];

  return (
    <Box mb={1.5} pl={1}>
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        {sub.name}
      </Typography>
      <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 4 }}>
        {warmupCount > 0 && (
          <Chip
            size="small"
            label={`${warmupCount} warmup`}
            variant="outlined"
          />
        )}
        {workingCount > 0 && (
          <Chip
            size="small"
            label={`${workingCount} working`}
            variant="outlined"
          />
        )}
        {dropRepsText && (
          <Chip size="small" label={`Drop ${dropRepsText}`} variant="outlined" />
        )}
        {miniSetRepsText && (
          <Chip size="small" label={`Triset ${miniSetRepsText}`} variant="outlined" />
        )}
        {!!tempoText && <Chip size="small" label={`Tempo ${tempoText}`} variant="outlined" />}
        {!!restText && <Chip size="small" label={`Rest ${restText} min`} variant="outlined" />}
      </Box>
      {sets.length > 0 && (
        <Box mt={1} pl={1}>
          {sets.map((s, i) => {
            const repsText = formatReps(s.reps);
            const rpeText = formatRange(s.rpe);
            const isWarmup = isWarmupSet(s);
            const percentText = isWarmup
              ? `${Math.round((s.percent || 0) * 100)}%`
              : null;
            return (
              <Typography
                key={i}
                variant="caption"
                color="textSecondary"
                style={{ display: "block" }}
              >
                {isWarmup ? "Warmup" : "Working"} {i + 1}:{" "}
                {[percentText, repsText, rpeText && `RPE ${rpeText}`]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            );
          })}
        </Box>
      )}
      {alternatives.length > 0 && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 4 }}
        >
          Alternatives: {alternatives.join(", ")}
        </Typography>
      )}
      {sub.notes && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 4, fontStyle: "italic" }}
        >
          {sub.notes}
        </Typography>
      )}
    </Box>
  );
};

const ImportPlans = ({
  communityId,
  authors = [],
  onImported,
}: {
  communityId: string;
  authors?: Author[];
  onImported?: () => void;
}) => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedSource, setUploadedSource] =
    useState<OnboardingUploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [community, setCommunity] = useState<Community | null>(null);
  const [useExternalGPT, setUseExternalGPT] = useState(false);
  const [preserveSourceText, setPreserveSourceText] = useState(true);
  const [selectedAuthorId, setSelectedAuthorId] = useState<string>("");

  // Plan-type mode and workout-specific state
  const [planMode, setPlanMode] = useState<"learning" | "workout">("learning");
  const [workoutDraft, setWorkoutDraft] = useState<WorkoutProgramDraft | null>(
    null
  );
  const [workoutFile, setWorkoutFile] = useState<File | null>(null);
  const [libraryHabitId, setLibraryHabitId] = useState<string>("");
  const [programNameOverride, setProgramNameOverride] = useState<string>("");
  const [workoutSubcategoryId, setWorkoutSubcategoryId] = useState<string>("");
  const [isExtractingWorkout, setIsExtractingWorkout] = useState(false);
  const [isImportingWorkout, setIsImportingWorkout] = useState(false);
  const [workoutStatusMessage, setWorkoutStatusMessage] = useState<string>("");
  const [workoutErrorMessage, setWorkoutErrorMessage] = useState<string>("");
  const [workoutProgress, setWorkoutProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);

  const [isImportingPlan, setIsImportingPlan] = useState(false);
  const [importStatus, setImportStatus] = useState<string>("");
  const [importError, setImportError] = useState<string>("");
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "info" | "success" | "error";
  }>({ open: false, message: "", severity: "info" });

  const showToast = (
    message: string,
    severity: "info" | "success" | "error"
  ) => setToast({ open: true, message, severity });

  const hideToast = () =>
    setToast((prev) => ({ ...prev, open: false }));

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const workoutFileInputRef = useRef<HTMLInputElement>(null);

  const { habits, loading: habitsLoading } = getAllHabits({ communityId });

  // Fetch community data on mount
  useEffect(() => {
    const fetchCommunity = async () => {
      if (!communityId) return;
      try {
        const doc = await firestore
          .collection(DB_PATH.COMMUNITY)
          .doc(communityId)
          .get();
        if (doc.exists) {
          setCommunity({ id: doc.id, ...doc.data() } as Community);
        }
      } catch (error) {
        console.error("Error fetching community:", error);
      }
    };
    fetchCommunity();
  }, [communityId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const derivePlanNameFromFile = (fileName: string): string => {
    const withoutExtension = fileName.replace(/\.[^/.]+$/, "").trim();
    const normalized = withoutExtension.replace(/[_-]+/g, " ").trim();
    return normalized || "Learning Plan";
  };

  const toOnboardingUploadedFile = async (
    file: File
  ): Promise<OnboardingUploadedFile> => {
    const lowerName = file.name.toLowerCase();
    const isTextFile =
      file.type === "text/plain" || lowerName.endsWith(".txt") || lowerName.endsWith(".md");

    if (isTextFile) {
      const text = await file.text();
      return {
        name: file.name,
        mimeType: "text/plain",
        text: text.slice(0, 40000),
      };
    }

    return {
      name: file.name,
      mimeType: file.type || "application/octet-stream",
      file,
    };
  };

  const generatePlanFromSource = async (
    source: OnboardingUploadedFile,
    options?: {
      planName?: string;
      additionalGuidance?: string;
    }
  ) => {
    const inferredPlanName =
      options?.planName?.trim() || derivePlanNameFromFile(source.name);

    const request = {
      sources: { uploadedFiles: [source] },
      planName: inferredPlanName,
      authorName: community?.author?.fullName || undefined,
      communityName: community?.title || undefined,
      additionalGuidance: options?.additionalGuidance?.trim() || undefined,
    };

    return preserveSourceText
      ? extractLearningPlanPreserveText(request)
      : generateSingleLearningPlan(request);
  };

  const inferRequestedTitle = (value: string): string | undefined => {
    const normalized = value.replace(/\s+/g, " ").trim();
    if (!normalized) return undefined;
    if (normalized.length > 80) return undefined;
    if (/[\n.!?]/.test(normalized)) return undefined;
    return normalized;
  };

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (useExternalGPT) return; // ignore uploads when using external GPT path
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadedFile(file);
    setUploadProgress("Reading file...");

    try {
      const source = await toOnboardingUploadedFile(file);
      setUploadedSource(source);

      setUploadProgress("Generating one learning plan from this file...");
      const generatedPlan = await generatePlanFromSource(source);
      setData(generatedPlan);

      const totalGeneratedSteps =
        generatedPlan.dayPlans?.reduce(
          (acc, dayPlan) => acc + (dayPlan.steps?.length || 0),
          0
        ) || 0;

      setMessages([
        {
          role: "assistant",
          content: `Created one learning plan from "${file.name}".\n\nPlan: ${generatedPlan.title}\nDays: ${generatedPlan.dayPlans?.length || 0}\nSteps: ${totalGeneratedSteps}\n\nClick "Import Plan" to save it, or type guidance to regenerate from the same file.`,
        },
      ]);
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Unknown error";
      setMessages([
        {
          role: "assistant",
          content: `Error generating a plan from this file: ${errorMsg}`,
        },
      ]);
      setUploadedFile(null);
      setUploadedSource(null);
    } finally {
      setIsUploading(false);
      setUploadProgress("");
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = async () => {
    setUploadedFile(null);
    setUploadedSource(null);
  };

  const sendMessage = async () => {
    if (useExternalGPT) {
      handleManualJSONImport();
      return;
    }
    if (isGenerating) return;

    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    const userMessage: Message | null = trimmedInput
      ? { role: "user", content: trimmedInput }
      : null;
    const newMessages = userMessage ? [...messages, userMessage] : messages;

    if (userMessage) setMessages(newMessages);
    setInputValue("");
    setIsGenerating(true);

    try {
      if (uploadedSource) {
        const generatedPlan = await generatePlanFromSource(uploadedSource, {
          planName: inferRequestedTitle(trimmedInput),
          additionalGuidance: trimmedInput || undefined,
        });

        const totalGeneratedSteps =
          generatedPlan.dayPlans?.reduce(
            (acc, dayPlan) => acc + (dayPlan.steps?.length || 0),
            0
          ) || 0;

        setData(generatedPlan);
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: `Regenerated plan from "${uploadedSource.name}".\n\nPlan: ${generatedPlan.title}\nDays: ${generatedPlan.dayPlans?.length || 0}\nSteps: ${totalGeneratedSteps}`,
          },
        ]);
        return;
      }

      if (!trimmedInput) return;

      const pastedRequest = {
        sources: { pastedContent: trimmedInput },
        planName: inferRequestedTitle(trimmedInput),
        authorName: community?.author?.fullName || undefined,
        communityName: community?.title || undefined,
        additionalGuidance: trimmedInput,
      };
      const generatedPlan = preserveSourceText
        ? await extractLearningPlanPreserveText(pastedRequest)
        : await generateSingleLearningPlan(pastedRequest);

      const totalGeneratedSteps =
        generatedPlan.dayPlans?.reduce(
          (acc, dayPlan) => acc + (dayPlan.steps?.length || 0),
          0
        ) || 0;

      setData(generatedPlan);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: `Generated one learning plan.\n\nPlan: ${generatedPlan.title}\nDays: ${generatedPlan.dayPlans?.length || 0}\nSteps: ${totalGeneratedSteps}`,
        },
      ]);
    } catch (error) {
      console.error("Error generating plan:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Unknown error";
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: uploadedSource
            ? `Error generating plan from the uploaded file: ${errorMsg}`
            : `Error: ${errorMsg}`,
        },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleManualJSONImport = () => {
    if (!inputValue.trim()) return;
    try {
      const parsed = parseJSONfromText(inputValue);
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      setData(first);
      setMessages([
        {
          role: "assistant",
          content: "JSON imported from external GPT link. Plan preview updated.",
        },
      ]);
    } catch (error) {
      console.error("Failed to parse JSON from manual input:", error);
      setMessages([
        {
          role: "assistant",
          content:
            "Could not parse the JSON you pasted. Please ensure it is valid JSON (array or object).",
        },
      ]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const parseDataToPlans = () => {
    if (!data) {
      console.log("No Data to parse");
      return;
    }
    const selectedAuthor = authors.find((a) => a.id === selectedAuthorId);
    const resolvedAuthorId = selectedAuthor?.id || data.authorId || "";

    const plan = {
      id: "",
      title: data.title || "",
      shortDescription: data.shortDescription || "",
      description: data.description || "",
      coverImageURL: data.coverImageURL || "",
      createdAt: new Date().toISOString(),
      categoryId: data.categoryId || "",
      authorId: resolvedAuthorId,
      communityId,
      habitsIds: data.habitsIds || [],
      isCompleted: data.isCompleted || false,
      likeCount: data.likeCount || 0,
      stepsCount: data.stepsCount || 0,
      stepsTaken: data.stepsTaken || 0,
      level: data.level || "Beginner",
      tags: data.tags || {},
    };

    const dayPlans =
      data.dayPlans?.map((dayPlan: any, index: number) => {
        const steps = Array.isArray(dayPlan.steps) ? dayPlan.steps : [];
        return {
          id: "",
          createdAt: new Date().toISOString(),
          title: dayPlan.title || "",
          description: dayPlan.description || "",
          steps: steps.map((step: any, stepIndex: number) => ({
            ...step,
            position:
              typeof step.position === "number"
                ? step.position
                : stepIndex + 1,
          })),
          isCompleted: dayPlan.isCompleted || false,
          communityId,
          planID: "",
          position:
            typeof dayPlan.position === "number"
              ? dayPlan.position
              : index + 1,
        };
      }) || [];

    return { plan, dayPlans };
  };

  const parseSteps = (dayPlans: any[]) => {
    if (!Array.isArray(dayPlans)) {
      console.error("Expected dayPlans to be an array, received:", dayPlans);
      return [];
    }

    const selectedAuthor = authors.find((a) => a.id === selectedAuthorId);

    return dayPlans.flatMap((dayPlan) => {
      const steps = Array.isArray(dayPlan.steps) ? dayPlan.steps : [];

      return steps.map((step: any, stepIndex: number) => {
        return {
          id: "",
          title: step.title || "",
          subtitle: step.subtitle || "",
          sectionTitle: step.sectionTitle || "",
          description: step.description || "",
          text: step.text || "",
          quote: step.quote || "",
          backgroundImageURL: step.backgroundImageURL || "",
          communityId,
          reference: step.reference || "",
          xpReward: step.xpReward || 0,
          isCompleted: step.isCompleted || false,
          type: step.type || "DEFAULT",
          dayPlanID: "",
          planID: "",
          authorID: step.authorID || selectedAuthor?.id || "",
          authorName: step.authorName || selectedAuthor?.fullName || "",
          questions: step.questions || [],
          ...(Array.isArray(step.images) && step.images.length
            ? { images: step.images }
            : {}),
          ...(Array.isArray(step.lists) && step.lists.length
            ? { lists: step.lists }
            : {}),
          ...(Array.isArray(step.callouts) && step.callouts.length
            ? { callouts: step.callouts }
            : {}),
          position:
            typeof step.position === "number" ? step.position : stepIndex + 1,
        };
      });
    });
  };

  const uploadPlans = async () => {
    if (!data) return;

    const parsedData = parseDataToPlans();
    if (!parsedData) return;

    setIsImportingPlan(true);
    setImportError("");
    setImportStatus("Saving plan...");
    showToast("Saving plan...", "info");

    try {
      const { plan, dayPlans } = parsedData;

      const planRef = await firestore.collection(DB_PATH.PLAN).add(plan);
      const planID = planRef.id;
      await planRef.update({ id: planID });

      let stepCount = 0;
      for (let i = 0; i < dayPlans.length; i += 1) {
        const dayPlan = dayPlans[i];
        const status = `Saving day ${i + 1} of ${dayPlans.length}...`;
        setImportStatus(status);
        showToast(status, "info");

        dayPlan.planID = planID;
        const dayPlanRef = await firestore
          .collection(DB_PATH.PLAN_DAY)
          .add(dayPlan);
        const dayPlanID = dayPlanRef.id;
        await dayPlanRef.update({ id: dayPlanID });

        const steps = parseSteps([{ ...dayPlan, id: dayPlanID, planID }]);
        for (const step of steps) {
          step.planID = planID;
          step.dayPlanID = dayPlanID;
          const stepRef = await firestore
            .collection(DB_PATH.PLAN_STEP)
            .add(step);
          await stepRef.update({ id: stepRef.id });
          stepCount += 1;
        }
      }

      const successMsg = `Imported "${plan.title}" with ${dayPlans.length} days and ${stepCount} steps.`;
      setImportStatus(successMsg);
      showToast(successMsg, "success");
      onImported?.();
      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error("Error uploading plans: ", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      setImportError(`Import failed: ${msg}`);
      showToast(`Import failed: ${msg}`, "error");
    } finally {
      setIsImportingPlan(false);
    }
  };

  const handleClose = async () => {
    await removeFile();

    setShow(false);
    setMessages([]);
    setData(null);
    setInputValue("");

    setWorkoutFile(null);
    setWorkoutDraft(null);
    setWorkoutStatusMessage("");
    setWorkoutErrorMessage("");
    setWorkoutProgress(null);
    setProgramNameOverride("");
    setWorkoutSubcategoryId("");
    setPlanMode("learning");

    setIsImportingPlan(false);
    setImportStatus("");
    setImportError("");
    setIsPreviewExpanded(false);

    setPreserveSourceText(true);
    setSelectedAuthorId("");
  };

  const handleWorkoutFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setWorkoutFile(file);
    setWorkoutErrorMessage("");
    setIsExtractingWorkout(true);
    setWorkoutProgress(null);
    setWorkoutStatusMessage("Reading PDF...");

    try {
      const source: OnboardingUploadedFile = {
        name: file.name,
        mimeType: file.type || "application/pdf",
        file,
      };

      setWorkoutStatusMessage("Extracting program outline (1 of 2)...");
      const generated = await generateWorkoutProgram({
        sources: { uploadedFiles: [source] },
        programName:
          programNameOverride.trim() || file.name.replace(/\.[^/.]+$/, ""),
        authorName: community?.author?.fullName,
        communityName: community?.title,
        onProgress: (event) => {
          if (event.stage === "outline_started") {
            setWorkoutStatusMessage("Extracting program outline (1 of 2)...");
            setWorkoutProgress(null);
          } else if (event.stage === "outline_done") {
            setWorkoutStatusMessage(
              `Outline ready. Extracting ${event.totalSessions} session${
                event.totalSessions === 1 ? "" : "s"
              } in parallel (2 of 2)...`
            );
            setWorkoutProgress({ completed: 0, total: event.totalSessions });
          } else if (event.stage === "session_done") {
            setWorkoutStatusMessage(
              `Extracting sessions: ${event.completed} of ${event.total} done...`
            );
            setWorkoutProgress({
              completed: event.completed,
              total: event.total,
            });
          }
        },
      });
      setWorkoutDraft(generated);
      const totalSessions = (generated.phases ?? []).reduce(
        (acc, phase) => acc + (phase.sessions?.length || 0),
        0
      );
      setWorkoutStatusMessage(
        `Extracted "${generated.title}" with ${generated.phases.length} phases and ${totalSessions} unique sessions.`
      );
      if (!workoutSubcategoryId) {
        const inferred = inferWorkoutSubcategory(
          generated.title,
          file.name,
          generated.tags
        );
        if (inferred) setWorkoutSubcategoryId(inferred);
      }
    } catch (error) {
      console.error("Workout extraction failed:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      setWorkoutErrorMessage(`Could not extract workout program: ${msg}`);
      setWorkoutFile(null);
      setWorkoutDraft(null);
    } finally {
      setIsExtractingWorkout(false);
      setWorkoutProgress(null);
      if (workoutFileInputRef.current) workoutFileInputRef.current.value = "";
    }
  };

  const handleImportWorkoutProgram = async () => {
    const selectedAuthor = authors.find((a) => a.id === selectedAuthorId);
    if (
      !workoutDraft ||
      !libraryHabitId ||
      !selectedAuthor ||
      !communityId
    )
      return;
    setIsImportingWorkout(true);
    setWorkoutErrorMessage("");
    setWorkoutProgress(null);
    setWorkoutStatusMessage("Preparing exercise library...");
    showToast("Saving workout program...", "info");

    try {
      const subcategory = findSubcategoryById(
        "exercise",
        workoutSubcategoryId
      );
      const result = await createWorkoutProgram({
        draft: workoutDraft,
        communityId,
        authorId: selectedAuthor.id,
        authorName: selectedAuthor.fullName ?? "",
        libraryHabitId,
        subcategoryId: subcategory?.id,
        subcategoryName: subcategory?.name,
        onProgress: (event) => {
          if (event.stage === "library_dedup") {
            setWorkoutStatusMessage(
              `Deduping exercise library: ${event.completed} of ${event.total} checked...`
            );
            setWorkoutProgress({
              completed: event.completed,
              total: event.total,
            });
          } else if (event.stage === "library_done") {
            setWorkoutStatusMessage(
              `Library ready: ${event.created} new, ${event.reused} reused. Building documents...`
            );
            setWorkoutProgress(null);
          } else if (event.stage === "building") {
            setWorkoutStatusMessage(
              `Built 1 plan, ${event.actionCount} action${
                event.actionCount === 1 ? "" : "s"
              }, ${event.dayPlanCount} day plan${
                event.dayPlanCount === 1 ? "" : "s"
              }, ${event.stepCount} step${
                event.stepCount === 1 ? "" : "s"
              }.`
            );
          } else if (event.stage === "committing") {
            setWorkoutStatusMessage(
              `Committing ${event.totalDocs} documents to Firestore...`
            );
          } else if (event.stage === "done") {
            setWorkoutStatusMessage(
              `Committed ${event.totalDocs} documents.`
            );
          }
        },
      });
      const successMsg =
        `Imported. Plan ${result.planId} with ${result.actionIds.length} sessions. ` +
        `Library: ${result.libraryExerciseCount} new, ${result.reusedExerciseCount} reused.`;
      setWorkoutStatusMessage(successMsg);
      showToast(successMsg, "success");
      onImported?.();
      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error("Workout import failed:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      setWorkoutErrorMessage(`Import failed: ${msg}`);
      showToast(`Import failed: ${msg}`, "error");
    } finally {
      setIsImportingWorkout(false);
      setWorkoutProgress(null);
    }
  };

  const removeWorkoutFile = () => {
    setWorkoutFile(null);
    setWorkoutDraft(null);
    setWorkoutStatusMessage("");
  };

  const totalWorkoutSessions = (workoutDraft?.phases ?? []).reduce(
    (acc, phase) => acc + (phase.sessions?.length || 0),
    0
  );
  const totalWorkoutExercises = (workoutDraft?.phases ?? []).reduce(
    (acc, phase) =>
      acc +
      (phase.sessions ?? []).reduce(
        (sAcc, session) =>
          sAcc +
          (session.blocks ?? []).reduce(
            (bAcc, block) => bAcc + (block.subactions?.length || 0),
            0
          ),
        0
      ),
    0
  );
  const failedWorkoutSessions = (workoutDraft?.phases ?? []).flatMap((phase) =>
    (phase.sessions ?? [])
      .filter((s) => s.extractionStatus === "failed")
      .map((s) => ({ phaseName: phase.name, sessionTitle: s.title }))
  );
  const retriedWorkoutSessions = (workoutDraft?.phases ?? []).flatMap((phase) =>
    (phase.sessions ?? [])
      .filter((s) => s.extractionStatus === "ok_after_retry")
      .map((s) => ({ phaseName: phase.name, sessionTitle: s.title }))
  );

  const renderMessage = (message: Message, index: number) => {
    const isUser = message.role === "user";
    return (
      <Box
        key={index}
        display="flex"
        justifyContent={isUser ? "flex-end" : "flex-start"}
        mb={2}
      >
        <Paper
          elevation={1}
          style={{
            padding: "12px 16px",
            maxWidth: "80%",
            backgroundColor: isUser ? "#1976d2" : "#f5f5f5",
            color: isUser ? "#fff" : "#333",
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          }}
        >
          <Typography
            variant="body2"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Calculate total steps count
  const totalSteps =
    data?.dayPlans?.reduce(
      (acc: number, day: any) => acc + (day.steps?.length || 0),
      0
    ) || 0;

  return (
    <div>
      <Button
        style={{ marginTop: 8 }}
        color="primary"
        variant="outlined"
        onClick={() => setShow(true)}
      >
        Import Plans with AI
      </Button>

      <Dialog
        open={show}
        onClose={handleClose}
        TransitionComponent={renderSlide}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { height: "80vh", display: "flex", flexDirection: "column" },
        }}
      >
        <DialogTitle>Create Plan with AI</DialogTitle>

        <Tabs
          value={planMode}
          onChange={(_, value) => setPlanMode(value)}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          style={{ borderBottom: "1px solid #e0e0e0" }}
        >
          <Tab label="Learning Plan" value="learning" />
          <Tab label="Workout Program" value="workout" />
        </Tabs>

        <DialogContent
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Author selection (shared across modes) */}
          <FormControl
            variant="outlined"
            fullWidth
            size="small"
            style={{ marginTop: 8, marginBottom: 16 }}
          >
            <InputLabel id="plan-author-label">Author</InputLabel>
            <Select
              labelId="plan-author-label"
              label="Author"
              value={selectedAuthorId}
              onChange={(e) => setSelectedAuthorId(e.target.value as string)}
            >
              {authors.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.fullName || a.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {planMode === "learning" && (
            <>
          {/* Mode toggle */}
          <Box
            mb={2}
            p={2}
            style={{
              backgroundColor: "#eef2ff",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="primary">
                Plan creation mode
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Toggle to paste JSON from the external GPT link instead of uploading here.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="caption" color="textSecondary">
                Upload & Chat
              </Typography>
              <Switch
                color="primary"
                checked={useExternalGPT}
                onChange={(e) => setUseExternalGPT(e.target.checked)}
              />
              <Typography variant="caption" color="textSecondary">
                Paste JSON
              </Typography>
            </Box>
          </Box>

          {/* External GPT helper */}
          {useExternalGPT && (
            <Box
              p={2}
              mb={2}
              style={{ backgroundColor: "#f0f7ff", borderRadius: 8 }}
            >
              <Typography variant="body1" gutterBottom>
                Generate the plan in ChatGPT, then paste the JSON here.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                href="https://chatgpt.com/g/g-692460ff59d0819196bcdab78b3faaad-plan-ai-app-designer"
                target="_blank"
                rel="noreferrer"
                style={{ marginBottom: 8 }}
              >
                Open Plan AI App Designer
              </Button>
              <Typography variant="body2" color="textSecondary">
                After you get the JSON response, paste it below and hit Send to import.
              </Typography>
            </Box>
          )}

          {/* File Upload Area */}
          {!useExternalGPT && !uploadedFile && messages.length === 0 && !isUploading && (
            <Box
              p={3}
              mb={2}
              style={{
                backgroundColor: "#f0f4ff",
                borderRadius: 8,
                border: "2px dashed #1976d2",
                textAlign: "center",
              }}
            >
              <DescriptionIcon
                style={{ fontSize: 48, color: "#1976d2", marginBottom: 8 }}
              />
              <Typography variant="body1" gutterBottom>
                Upload a book or document as reference (optional)
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Supported formats: PDF, TXT, MD, DOCX
              </Typography>
              <Box mt={1} mb={1} display="flex" justifyContent="center">
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={preserveSourceText}
                      onChange={(e) => setPreserveSourceText(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Keep text verbatim from source (split into chapters/modules, no AI rewriting)
                    </Typography>
                  }
                />
              </Box>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.txt,.md,.docx"
                style={{ display: "none" }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                style={{ marginTop: 8 }}
              >
                Select file
              </Button>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                Or simply describe the plan you want to create
              </Typography>
            </Box>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <Box p={3} mb={2} textAlign="center">
              <CircularProgress size={40} />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                {uploadProgress || "Processing..."}
              </Typography>
              <LinearProgress style={{ marginTop: 16 }} />
            </Box>
          )}

          {/* Uploaded File Indicator */}
          {!useExternalGPT && uploadedFile && !isUploading && (
            <Box mb={2}>
              <Chip
                icon={<DescriptionIcon />}
                label={`${uploadedFile.name} (analyzed source)`}
                onDelete={removeFile}
                deleteIcon={<CloseIcon />}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}

          {/* Chat Messages Area */}
          <Box
            flex={isPreviewExpanded && data ? 0 : 1}
            overflow="auto"
            p={2}
            style={{
              backgroundColor: "#fafafa",
              borderRadius: 8,
              display: isPreviewExpanded && data ? "none" : "block",
            }}
          >
            {!useExternalGPT && messages.length === 0 && !uploadedFile && !isUploading && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  Describe the learning plan you want to create.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 8 }}
                >
                  Example: "Create a plan by David Goggins for the Mental
                  Toughness community called Forge the Mind"
                </Typography>
              </Box>
            )}

            {messages.map((msg, idx) => renderMessage(msg, idx))}

            {isGenerating && (
              <Box
                display="flex"
                alignItems="center"
                ml={1}
                style={{ gap: 8 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Generating...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Plan Preview */}
          {data && (
            <Box
              mt={2}
              p={2}
              flex={isPreviewExpanded ? 1 : "0 0 auto"}
              display="flex"
              flexDirection="column"
              style={{
                backgroundColor: "#fafafa",
                borderRadius: 8,
                border: "1px solid #c8e6c9",
                maxHeight: isPreviewExpanded ? "none" : 360,
                minHeight: isPreviewExpanded ? 0 : undefined,
                overflowY: "auto",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle2" color="primary">
                  Plan ready to import
                </Typography>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setIsPreviewExpanded((v) => !v)}
                >
                  {isPreviewExpanded ? "Collapse preview" : "Expand preview"}
                </Button>
              </Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {data.title}
              </Typography>
              {data.shortDescription && (
                <Typography variant="body2" color="textSecondary">
                  {data.shortDescription}
                </Typography>
              )}
              <Box
                display="flex"
                flexWrap="wrap"
                style={{ gap: 6, marginTop: 8 }}
              >
                <Chip
                  size="small"
                  label={`${data.dayPlans?.length || 0} day${
                    (data.dayPlans?.length || 0) === 1 ? "" : "s"
                  }`}
                />
                <Chip
                  size="small"
                  label={`${totalSteps} step${totalSteps === 1 ? "" : "s"}`}
                />
                {data.level && (
                  <Chip
                    size="small"
                    label={data.level}
                    variant="outlined"
                  />
                )}
                {data.author && (
                  <Chip
                    size="small"
                    label={`By ${data.author}`}
                    variant="outlined"
                  />
                )}
                {data.categoryId && (
                  <Chip
                    size="small"
                    label={data.categoryId}
                    variant="outlined"
                  />
                )}
              </Box>
              {data.description && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{
                    display: "block",
                    marginTop: 8,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {data.description}
                </Typography>
              )}

              <Box mt={2}>
                {(data.dayPlans ?? []).map((day: any, di: number) => {
                  const steps: any[] = Array.isArray(day.steps)
                    ? day.steps
                    : [];
                  return (
                    <Accordion
                      key={di}
                      elevation={0}
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: 6,
                        marginBottom: 6,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          style={{ gap: 8, width: "100%" }}
                        >
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600, flex: 1 }}
                          >
                            Day {di + 1}: {day.title || "Untitled"}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${steps.length} step${
                              steps.length === 1 ? "" : "s"
                            }`}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        style={{ display: "block", paddingTop: 0 }}
                      >
                        {day.description && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{
                              display: "block",
                              marginBottom: 12,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {day.description}
                          </Typography>
                        )}
                        {steps.length === 0 && (
                          <Typography variant="caption" color="error">
                            No steps generated for this day.
                          </Typography>
                        )}
                        {steps.map((step, si) => (
                          <StepPreview key={si} step={step} index={si} />
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Import progress / success */}
          {(isImportingPlan || (importStatus && !importError)) && (
            <Box
              mt={2}
              p={2}
              style={{
                backgroundColor: isImportingPlan ? "#e3f2fd" : "#e8f5e9",
                borderRadius: 8,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                {isImportingPlan && <CircularProgress size={18} />}
                <Typography variant="body2">
                  {importStatus || "Importing..."}
                </Typography>
              </Box>
              {isImportingPlan && (
                <LinearProgress style={{ marginTop: 12 }} />
              )}
            </Box>
          )}

          {/* Import error */}
          {importError && (
            <Box
              mt={2}
              p={2}
              style={{ backgroundColor: "#ffebee", borderRadius: 8 }}
            >
              <Typography variant="body2" color="error">
                {importError}
              </Typography>
            </Box>
          )}

          {/* Input Area */}
          <Box display="flex" alignItems="center" mt={2} style={{ gap: 8 }}>
            {/* Hidden file input for adding files later */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.txt,.md,.docx"
              style={{ display: "none" }}
            />
            {!useExternalGPT && messages.length > 0 && !uploadedFile && (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isGenerating}
                size="small"
              >
                <AttachFileIcon />
              </IconButton>
            )}
            <TextField
              fullWidth
              variant="outlined"
              placeholder={
                useExternalGPT
                  ? "Paste JSON from the GPT link here..."
                  : "Type your message..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isGenerating || isUploading}
              size="small"
              multiline
              maxRows={useExternalGPT ? 8 : 3}
            />
            <IconButton
              color="primary"
              onClick={sendMessage}
              disabled={!inputValue.trim() || isGenerating || isUploading}
            >
              <SendIcon />
            </IconButton>
          </Box>
            </>
          )}

          {planMode === "workout" && (
            <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
              <Typography variant="body2" color="textSecondary">
                Upload a structured workout-program PDF. The system extracts
                phases, sessions, blocks, and exercise prescriptions, then
                writes a Plan plus dedicated Actions and a deduped exercise
                library.
              </Typography>

              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="library-habit-label">
                  Library habit (hosts the exercise library)
                </InputLabel>
                <Select
                  labelId="library-habit-label"
                  label="Library habit (hosts the exercise library)"
                  value={libraryHabitId}
                  onChange={(e) =>
                    setLibraryHabitId(e.target.value as string)
                  }
                  disabled={
                    habitsLoading ||
                    isExtractingWorkout ||
                    isImportingWorkout
                  }
                >
                  {habits.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.habitName || h.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="workout-subcategory-label">
                  Subcategory (groups the plan in HabitDetailScreen)
                </InputLabel>
                <Select
                  labelId="workout-subcategory-label"
                  label="Subcategory (groups the plan in HabitDetailScreen)"
                  value={workoutSubcategoryId}
                  onChange={(e) =>
                    setWorkoutSubcategoryId(e.target.value as string)
                  }
                  disabled={isExtractingWorkout || isImportingWorkout}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {PLAN_SUBCATEGORIES.exercise.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                variant="outlined"
                size="small"
                label="Program name override (optional)"
                value={programNameOverride}
                onChange={(e) => setProgramNameOverride(e.target.value)}
                disabled={isExtractingWorkout || isImportingWorkout}
                fullWidth
              />

              {!workoutFile && !isExtractingWorkout && (
                <Box
                  p={3}
                  style={{
                    backgroundColor: "#f0f4ff",
                    borderRadius: 8,
                    border: "2px dashed #1976d2",
                    textAlign: "center",
                  }}
                >
                  <DescriptionIcon
                    style={{
                      fontSize: 48,
                      color: "#1976d2",
                      marginBottom: 8,
                    }}
                  />
                  <Typography variant="body1" gutterBottom>
                    Upload a workout-program PDF
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Best results with table-structured programs (phases,
                    weeks, sets, reps, tempo, RPE).
                  </Typography>
                  <input
                    type="file"
                    ref={workoutFileInputRef}
                    onChange={handleWorkoutFileUpload}
                    accept=".pdf"
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AttachFileIcon />}
                    onClick={() => workoutFileInputRef.current?.click()}
                    disabled={!libraryHabitId}
                    style={{ marginTop: 8 }}
                  >
                    Select PDF
                  </Button>
                  {!libraryHabitId && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      Select a library habit first.
                    </Typography>
                  )}
                </Box>
              )}

              {isExtractingWorkout && (
                <Box p={3} textAlign="center">
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 16 }}
                  >
                    {workoutStatusMessage || "Processing..."}
                  </Typography>
                  {workoutProgress && workoutProgress.total > 0 ? (
                    <LinearProgress
                      variant="determinate"
                      value={
                        (workoutProgress.completed / workoutProgress.total) *
                        100
                      }
                      style={{ marginTop: 16 }}
                    />
                  ) : (
                    <LinearProgress style={{ marginTop: 16 }} />
                  )}
                </Box>
              )}

              {isImportingWorkout && (
                <Box
                  p={3}
                  textAlign="center"
                  style={{ backgroundColor: "#e3f2fd", borderRadius: 8 }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 16 }}
                  >
                    {workoutStatusMessage || "Saving..."}
                  </Typography>
                  {workoutProgress && workoutProgress.total > 0 ? (
                    <LinearProgress
                      variant="determinate"
                      value={
                        (workoutProgress.completed / workoutProgress.total) *
                        100
                      }
                      style={{ marginTop: 16 }}
                    />
                  ) : (
                    <LinearProgress style={{ marginTop: 16 }} />
                  )}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginTop: 12 }}
                  >
                    This may take a minute. Don't close the window.
                  </Typography>
                </Box>
              )}

              {workoutFile && !isExtractingWorkout && (
                <Box>
                  <Chip
                    icon={<DescriptionIcon />}
                    label={workoutFile.name}
                    onDelete={removeWorkoutFile}
                    deleteIcon={<CloseIcon />}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}

              {workoutStatusMessage &&
                !workoutErrorMessage &&
                !isExtractingWorkout &&
                !isImportingWorkout && (
                  <Paper
                    elevation={0}
                    style={{
                      padding: 12,
                      backgroundColor: "#e8f5e9",
                    }}
                  >
                    <Typography variant="body2">
                      {workoutStatusMessage}
                    </Typography>
                  </Paper>
                )}

              {workoutErrorMessage && (
                <Paper
                  elevation={0}
                  style={{ padding: 12, backgroundColor: "#ffebee" }}
                >
                  <Typography variant="body2" color="error">
                    {workoutErrorMessage}
                  </Typography>
                </Paper>
              )}

              {workoutDraft && failedWorkoutSessions.length > 0 && (
                <Paper
                  elevation={0}
                  style={{
                    padding: 12,
                    backgroundColor: "#fff3e0",
                    borderLeft: "4px solid #ef6c00",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{ color: "#ef6c00", fontWeight: 600 }}
                  >
                    {failedWorkoutSessions.length} session
                    {failedWorkoutSessions.length === 1 ? "" : "s"} failed to
                    extract
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginTop: 4 }}
                  >
                    These will be imported with 0 exercises. Re-upload the PDF
                    to retry, or proceed and fill them in manually after import.
                  </Typography>
                  <Box mt={1}>
                    {failedWorkoutSessions.map((f, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        style={{ display: "block" }}
                      >
                        • {f.phaseName} → {f.sessionTitle}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              )}

              {workoutDraft && retriedWorkoutSessions.length > 0 && (
                <Paper
                  elevation={0}
                  style={{
                    padding: 12,
                    backgroundColor: "#e3f2fd",
                    borderLeft: "4px solid #1976d2",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block" }}
                  >
                    {retriedWorkoutSessions.length} session
                    {retriedWorkoutSessions.length === 1 ? "" : "s"} required a
                    retry to extract — double-check{" "}
                    {retriedWorkoutSessions
                      .map((s) => `${s.phaseName} → ${s.sessionTitle}`)
                      .join("; ")}
                    .
                  </Typography>
                </Paper>
              )}

              {workoutDraft && (
                <Paper
                  elevation={0}
                  style={{ padding: 16, backgroundColor: "#fafafa" }}
                >
                  <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                    {workoutDraft.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {workoutDraft.shortDescription}
                  </Typography>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: 8, marginTop: 12 }}
                  >
                    <Chip
                      label={`${workoutDraft.phases.length} phases`}
                      size="small"
                    />
                    <Chip
                      label={`${totalWorkoutSessions} sessions`}
                      size="small"
                    />
                    <Chip
                      label={`${totalWorkoutExercises} exercise rows`}
                      size="small"
                    />
                    {workoutDraft.warmupProtocol && (
                      <Chip
                        label="Warmup protocol"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box mt={2}>
                    {workoutDraft.phases.map((phase) => (
                      <Box key={phase.phaseNumber} mb={2}>
                        <Typography
                          variant="subtitle2"
                          style={{ marginBottom: 6 }}
                        >
                          {phase.name} (weeks {phase.weekRange[0]}-
                          {phase.weekRange[1]})
                        </Typography>
                        {phase.sessions.map((session) => {
                          const exerciseCount = (session.blocks ?? []).reduce(
                            (acc, b) => acc + (b.subactions?.length || 0),
                            0
                          );
                          const groupCount = (session.blocks ?? []).filter(
                            (b) => b.type !== "single"
                          ).length;
                          return (
                            <Accordion
                              key={`${phase.phaseNumber}-${session.position}`}
                              elevation={0}
                              style={{
                                backgroundColor: "#fff",
                                border: "1px solid #e0e0e0",
                                borderRadius: 6,
                                marginBottom: 6,
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: 8, width: "100%" }}
                                >
                                  <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600, flex: 1 }}
                                  >
                                    {session.title}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${exerciseCount} exercises`}
                                  />
                                  {session.extractionStatus === "failed" && (
                                    <Chip
                                      size="small"
                                      label="Extraction failed"
                                      style={{
                                        backgroundColor: "#ef6c00",
                                        color: "#fff",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                  {session.extractionStatus ===
                                    "ok_after_retry" && (
                                    <Chip
                                      size="small"
                                      label="Recovered on retry"
                                      style={{
                                        backgroundColor: "#1976d2",
                                        color: "#fff",
                                      }}
                                    />
                                  )}
                                  {groupCount > 0 && (
                                    <Chip
                                      size="small"
                                      label={`${groupCount} group${
                                        groupCount === 1 ? "" : "s"
                                      }`}
                                      color="primary"
                                      variant="outlined"
                                    />
                                  )}
                                  {session.restDayAfter !== "none" && (
                                    <Chip
                                      size="small"
                                      label={`Rest: ${session.restDayAfter}`}
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                style={{ display: "block", paddingTop: 0 }}
                              >
                                {session.description && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{
                                      display: "block",
                                      marginBottom: 12,
                                    }}
                                  >
                                    {session.description}
                                  </Typography>
                                )}
                                {(session.blocks ?? []).length === 0 && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                  >
                                    No exercises extracted for this session.
                                  </Typography>
                                )}
                                {(session.blocks ?? []).map((block, bi) => {
                                  const header = blockHeaderLabel(block);
                                  return (
                                    <Box key={bi} mb={1.5}>
                                      {header && (
                                        <Chip
                                          size="small"
                                          label={header}
                                          color="primary"
                                          style={{
                                            marginBottom: 8,
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                      {(block.subactions ?? []).map(
                                        (sub: any, si: number) => (
                                          <SubactionPreview
                                            key={`${bi}-${si}`}
                                            sub={sub}
                                          />
                                        )
                                      )}
                                    </Box>
                                  );
                                })}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            color="default"
            disabled={isImportingWorkout || isImportingPlan}
          >
            Cancel
          </Button>
          {planMode === "learning" ? (
            <Button
              onClick={uploadPlans}
              color="primary"
              variant="contained"
              disabled={!data || !selectedAuthorId || isImportingPlan}
            >
              {isImportingPlan ? "Importing..." : "Import Plan"}
            </Button>
          ) : (
            <Button
              onClick={handleImportWorkoutProgram}
              color="primary"
              variant="contained"
              disabled={
                !workoutDraft ||
                !libraryHabitId ||
                !selectedAuthorId ||
                isImportingWorkout
              }
            >
              {isImportingWorkout ? "Importing..." : "Import Program"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={toast.severity === "info" ? null : 5000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          if (toast.severity === "info") return;
          hideToast();
        }}
      >
        <SnackbarContent
          style={{
            backgroundColor:
              toast.severity === "success"
                ? "#2e7d32"
                : toast.severity === "error"
                ? "#c62828"
                : "#1976d2",
            minWidth: 280,
          }}
          message={
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              {toast.severity === "info" && (
                <CircularProgress size={18} style={{ color: "#fff" }} />
              )}
              <Typography variant="body2" style={{ color: "#fff" }}>
                {toast.message}
              </Typography>
            </Box>
          }
          action={
            toast.severity !== "info" ? (
              <IconButton
                size="small"
                onClick={hideToast}
                style={{ color: "#fff" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null
          }
        />
      </Snackbar>
    </div>
  );
};

export default ImportPlans;
